import React, { forwardRef, useEffect } from 'react'
import moment from 'moment'
import { Controller, useFormContext } from 'react-hook-form'
import { Task } from '@core/entities/task/Task'
import { Card, TextField } from '@common/material'
import { DatePicker } from '@common/material/DatePicker'
import { Flex } from '@fundamentals'
import { localizedStrings } from '@core/strings'
import { CreateTaskFormFields } from '@modules/tasks/createTask/types'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'
import {
  ProjectLocationPicker,
  ProjectPicker,
  TaskAssignUserPicker,
  TaskPriorityPicker,
  TaskTemplatePicker,
} from '@elements/Pickers'
import { useGetProjectLocations } from '@core/react-query/features/projects/locations'
import { MAX_TASK_SUMMARY_CHARACTERS } from '@core/components/templateAnswersForm'
import { QuestionsForm } from '@modules/template-form'
import { VirtualizedQuestionGroupListHandle } from '@elements/Questions/VirtualizedQuestionGroupList'
import { AttachmentTypes } from '@core/components/templateAnswersForm/types'
import useConstants from '@core/providers/useConstants'
import { ITaskTemplate } from '@core/entities/task/TaskTemplate'

const toProofType = (type: string) => {
  if (type.includes('application/pdf') || type.includes('PDF')) {
    return 'application/pdf'
  }
  if (type.includes('video')) {
    return type
  }
  return 'image'
}

type TaskFormProps = {
  organisationId: number
  stepId: string
  disableProjectSelect?: boolean
  disableTaskTemplateSelect?: boolean
  disableCardBorder?: boolean
  hideProjectSelect?: boolean
  priorityLabel?: string
  taskTemplates?: Array<ITaskTemplate>
  attachmentTypes?: AttachmentTypes[]
}

export const TaskForm = forwardRef<
  VirtualizedQuestionGroupListHandle,
  TaskFormProps
>(function TaskForm(
  {
    organisationId,
    stepId,
    disableProjectSelect,
    disableTaskTemplateSelect,
    disableCardBorder,
    hideProjectSelect,
    priorityLabel,
    taskTemplates,
    attachmentTypes,
  },
  ref,
) {
  const { constants } = useConstants()
  const { getValues, control, watch, setValue } =
    useFormContext<CreateTaskFormFields>()
  const { template, projectId, draftId } = getValues()

  const watchPriority = watch('priority')
  const watchQuestions = watch('questions')

  const { data: projectLocations } = useGetProjectLocations({
    projectId,
    statuses: ['ACTIVE'],
  })

  // Set the default priority when the form initializes
  useEffect(() => {
    if (!watchPriority && template?.getPriorityDefaultValue()) {
      setValue('priority', template.getPriorityDefaultValue() as any)
    }

    const priority = watchPriority || template?.getPriorityDefaultValue()
    const dueDateConfig = template?.getDueDateConfig()

    // If the priority sets the due date, calculate the due date
    if (
      priority &&
      !getValues('dueDate') &&
      dueDateConfig?.prioritySetsTheDueDate
    ) {
      const dueDate = Task.calculateDueDate({
        priority,
        dueDateConfig,
      })
      setValue('dueDate', dueDate as any)
    }
  }, [template?.currentVersion?.templateId])

  return (
    <Card
      sx={{ p: 2, mb: 4, ...(disableCardBorder && { border: 'none', p: 1 }) }}
    >
      <Flex flexDirection='column' gap={1}>
        {!hideProjectSelect && (
          <Controller
            name='projectId'
            rules={{ required: localizedStrings.selectProject }}
            render={({ field: { value, onChange, ...field }, fieldState }) => {
              return (
                <ProjectPicker
                  organisationId={organisationId}
                  value={value}
                  onChange={(e) => {
                    setValue('template', null)
                    onChange(e)
                  }}
                  disabled={!!draftId || disableProjectSelect}
                  textFieldProps={{
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                    fullWidth: true,
                    ...field,
                  }}
                />
              )
            }}
          />
        )}

        {projectId && (
          <Controller
            name='template'
            rules={{ required: localizedStrings.selectTemplate }}
            render={({ field, fieldState }) => {
              return (
                <TaskTemplatePicker
                  clearSelectionOnOpen={false}
                  organisationId={organisationId}
                  projectId={projectId}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ mt: 2 }}
                  disabled={!!draftId || disableTaskTemplateSelect}
                  fullWidth
                  options={taskTemplates}
                  {...field}
                />
              )
            }}
          />
        )}
      </Flex>

      {template && (
        <Flex flexDirection='column' gap={3} my={2}>
          <Controller
            name='summary'
            rules={{ required: localizedStrings.pleaseEnterADescription }}
            render={({ field: { onChange, ...field }, fieldState }) => {
              return (
                <TextField
                  label={localizedStrings.description}
                  disabled={!projectId}
                  multiline
                  rows={2}
                  placeholder={localizedStrings.description}
                  data-test='task-summary-input'
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    `${
                      field.value?.length || 0
                    }/${MAX_TASK_SUMMARY_CHARACTERS} characters`
                  }
                  onChange={(e) =>
                    e.target.value.length <= MAX_TASK_SUMMARY_CHARACTERS &&
                    onChange(e)
                  }
                  slotProps={{
                    formHelperText: {
                      error: !!fieldState.error,
                    },
                  }}
                  fullWidth
                  required
                  {...field}
                />
              )
            }}
          />

          <Controller
            control={control}
            name='priority'
            rules={{ required: localizedStrings.pleaseSelectPriority }}
            render={({ field, fieldState }) => (
              <TaskPriorityPicker
                label={priorityLabel}
                value={field.value}
                onChange={(value) => {
                  const dueDateConfig = template?.getDueDateConfig()

                  if (dueDateConfig?.prioritySetsTheDueDate) {
                    const dueDate = Task.calculateDueDate({
                      priority: value,
                      dueDateConfig,
                    })
                    setValue('dueDate', dueDate)
                  }

                  field.onChange(value)
                }}
                priorityOptions={template?.getPriorityOptions()}
                error={fieldState.error}
              />
            )}
          />

          <Controller
            name='dueDate'
            rules={{ required: template?.getDueDateRequired() }}
            render={({ field: { value, onChange, ...field }, fieldState }) => {
              return (
                <DatePicker
                  dataTest='date-picker-due-date'
                  disablePast
                  value={value ? moment(value) : undefined}
                  onChange={(rawDate) => {
                    if (!rawDate) {
                      onChange(undefined)
                      return
                    }

                    const dueDate = Task.calculateDueDate({
                      rawDate,
                      priority: watchPriority,
                      dueDateConfig: template?.getDueDateConfig(),
                    })
                    onChange(dueDate)
                  }}
                  withTime={true}
                  label={localizedStrings.dueDate}
                  error={!!fieldState.error}
                  sx={{ flex: 1 }}
                  required={template?.getDueDateRequired()}
                  disabled={
                    template?.getDueDatePrioritySetsTheDueDate() &&
                    !template?.currentVersion?.properties?.dueDateConfig
                      ?.prioritySetsTheDueDateConfig
                      ?.allowDefaultsToBeOverridden
                  }
                  {...field}
                />
              )
            }}
          />

          {projectLocations?.enabled ? (
            <Controller
              name='projectLocations.locations'
              rules={{
                required: localizedStrings.pleaseSelectALocation,
                validate: (value) =>
                  !!value?.filter((l) => l.locationNodeId).length ||
                  localizedStrings.pleaseSelectALocation,
              }}
              render={({ field: { onChange, ...field }, fieldState }) => (
                <ProjectLocationPicker
                  projectId={projectId}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onChange={onChange}
                  {...field}
                />
              )}
            />
          ) : (
            <Controller
              name='projectLocations.locations'
              rules={{
                validate: (value) =>
                  !!value?.[0]?.locationName ||
                  localizedStrings.pleaseEnterALocation,
              }}
              render={({
                field: { onChange, value, ...field },
                fieldState,
              }) => (
                <TextField
                  label={localizedStrings.location}
                  value={value?.[0]?.locationName ?? ''}
                  placeholder={localizedStrings.location}
                  data-test='select-location'
                  onChange={(e) => {
                    const locationName = e.target.value
                    onChange(locationName ? [{ locationName }] : null)
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  {...field}
                />
              )}
            />
          )}

          <Controller
            name='attachments'
            control={control}
            rules={{ required: template?.getAttachmentRequired() }}
            render={({ field: { value, onChange, ...field } }) => {
              const attachments = value?.data || []

              return (
                <AttachmentInput
                  heading={localizedStrings.attachments}
                  enableLinkSiteBoxDocuments={true}
                  projectId={projectId}
                  inputProps={{
                    value: attachments.map((attachment) => ({
                      ...attachment,
                      type: toProofType(attachment.type),
                    })),
                    onChange: (proofs) => {
                      onChange({ data: proofs })
                      field.onBlur()
                    },
                  }}
                  attachmentTypes={
                    attachmentTypes || [
                      AttachmentTypes.IMAGE,
                      AttachmentTypes.PDF,
                    ]
                  }
                  maxFileSize={constants.max_question_attachment_size_in_bytes}
                  required={template?.getAttachmentRequired()}
                  dataTest='task-attachment-input'
                  {...field}
                />
              )
            }}
          />

          <Controller
            control={control}
            name='assigneeUser'
            rules={{ required: localizedStrings.pleaseSelectAnAssignee }}
            render={({ field, fieldState }) => (
              <TaskAssignUserPicker
                projectId={projectId}
                templateId={template?.currentVersion?.templateId}
                stepId={stepId}
                value={field.value}
                onChange={field.onChange}
                error={!!fieldState.error}
              />
            )}
          />
        </Flex>
      )}

      {watchQuestions?.length > 0 && (
        <Flex flexDirection='column' gap={2}>
          <QuestionsForm
            ref={ref}
            organisationId={organisationId}
            questionsFormKey='questions'
          />
        </Flex>
      )}
    </Card>
  )
})
